import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  ExtensionTag,
  getTextSelection,
  keyBinding,
  KeyBindingProps,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  NamedShortcut,
  PrimitiveSelection,
  ProsemirrorAttributes,
  Static,
  toggleMark,
} from '@remirror/core';
import { CoreIcon } from '@remirror/icons';
import React from 'react';
import { InputRule, markInputRule } from 'remirror';

import { WFormatTypeProperty } from './_interface';

const CREATE_COMMAND_LABEL = 'Toggle Subscript';

const toggleWFormatSuperscriptOptions: Remirror.CommandDecoratorOptions = {
  icon: 'superscript2',
  label: ({ t }) => t(CREATE_COMMAND_LABEL),
  shortcut: NamedShortcut.Superscript,
};

export type WFormatSuperscriptExtensionAttributes = ProsemirrorAttributes<{
  /**
   * The Mark type.
   */
  type?: WFormatTypeProperty;
}>;

export interface WFormatSuperscriptOptions {
  /**
   * Require set the mark type property for this extension.
   */
  type?: Static<WFormatTypeProperty>;
  defaultType?: Static<WFormatTypeProperty>;
}

/**
 * When added to your editor it will provide the `toggleBold` command which makes the
 * text under the cursor / or at the provided position range bold.
 */
@extension<WFormatSuperscriptOptions>({
  defaultOptions: {
    type: undefined,
    defaultType: undefined,
  },
  staticKeys: ['defaultType', 'type'],
})
export class WFormatSuperscriptExtension extends MarkExtension<WFormatSuperscriptOptions> {
  get name() {
    return 'wFormatSuperscript' as const;
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.FontStyle];
  }

  createMarkSpec(extra: ApplySchemaAttributes, override: MarkSpecOverride): MarkExtensionSpec {
    return {
      ...override,
      attrs: {
        ...extra.defaults(),
        type: { default: this.options.defaultType },
      },
      parseDOM: [
        {
          tag: `w-format[type=superscript]`,
          getAttrs: (element: string | Node) => ({
            ...extra.parse(element),
            type: 'superscript',
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (mark) => {
        const { type } = this.options;
        return ['w-format', { type: mark.attrs.type.toString() }, 0];
      },
    };
  }

  createInputRules(): InputRule[] {
    return [
      markInputRule({
        regexp: /(?:\*\*|__)([^*_]+)(?:\*\*|__)$/,
        type: this.type,
        ignoreWhitespace: true,
      }),
    ];
  }

  @command(toggleWFormatSuperscriptOptions)
  toggleWFormatSuperscript(
    attrs?: WFormatSuperscriptExtensionAttributes,
    selection?: PrimitiveSelection,
  ): CommandFunction {
    return toggleMark({ type: this.type, selection, attrs: { ...attrs, type: 'superscript' } });
  }

  @command()
  setWFormatSuperscript(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);
      dispatch?.(tr.addMark(from, to, this.type.create()));

      return true;
    };
  }

  @command()
  removeWFormatSuperscript(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);

      if (!tr.doc.rangeHasMark(from, to, this.type)) {
        return false;
      }

      dispatch?.(tr.removeMark(from, to, this.type));

      return true;
    };
  }

  @keyBinding({ shortcut: NamedShortcut.Superscript, command: 'toggleWFormatSuperscript' })
  shortcut(props: KeyBindingProps): boolean {
    return this.toggleWFormatSuperscript()(props);
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Remirror {
    interface AllExtensions {
      wFormatSuperscript: WFormatSuperscriptExtension;
    }
  }
}
